import { Column, Stack } from '@shipt/design-system-layouts'
import { Body, Headline } from '@shipt/design-system-typography'
import styled from 'styled-components'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { type StorySubsectionEmbedded } from '@/features/cms/components/CMS/types'
import { screenSizes } from '@shipt/design-system-themes'

export const MultiStoryRefreshCards = ({
  stories,
  text_color,
}: {
  stories: StorySubsectionEmbedded[]
  text_color: string
}) =>
  stories.map((story) => {
    const { image_type, background_color, image, heading, subheading } = story.data
    return image_type === 'avatar' ? (
      <RefreshAvatarStory
        key={heading}
        direction="row"
        textColor={text_color}
        spacing={{ base: 'lg', md: 'xl' }}
        align="start"
      >
        <RefreshImageContainer data-story-type={image_type}>
          <Image
            alt=""
            src={image.src}
            width={image.width}
            height={image.height}
            style={{
              objectFit: 'contain',
            }}
          />
        </RefreshImageContainer>
        <Column align="start" display={{ base: 'none', md: 'flex' }}>
          <Body size="xl" strong>
            {heading}
          </Body>
          <Body>{subheading ?? ''}</Body>
        </Column>
        <Column display={{ base: 'flex', md: 'none' }}>
          <Body strong>{heading}</Body>
          <Body size="md">{subheading ?? ''}</Body>
        </Column>
      </RefreshAvatarStory>
    ) : (
      <RefreshImageStory
        key={heading}
        direction="column"
        backgroundColor={background_color ?? ''}
        align="center"
      >
        <RefreshImageContainer data-story-type={image_type}>
          <Image alt="" src={image.src} width={image.width} height={image.height} />
        </RefreshImageContainer>
        <Column margin={['xl', 'xl', 'xxl']} spacing="lg" display={{ base: 'none', md: 'flex' }}>
          <Headline>{heading}</Headline>
          <Body variant="secondary">{subheading ?? ''}</Body>
        </Column>
        <Column margin={['xl', 'xl', 'xxl']} spacing="lg" display={{ base: 'flex', md: 'none' }}>
          <Headline size="md">{heading}</Headline>
          <Body size="md" variant="secondary">
            {subheading ?? ''}
          </Body>
        </Column>
      </RefreshImageStory>
    )
  })

const RefreshAvatarStory = styled(Stack)<{
  textColor: string
}>`
  p,
  div {
    color: ${({ textColor }) => textColor};
  }
`

const RefreshImageStory = styled(Stack)<{
  backgroundColor: string
}>`
  border-radius: 1rem;
  background-color: ${({ backgroundColor }) => backgroundColor};
  height: 100%;
  min-height: 400px;
  max-width: 300px;
  margin-right: 8px;

  @media ${screenSizes.tablet} {
    min-height: 495px;
    max-width: 405px;
  }
`

const RefreshImageContainer = styled(Stack)<{
  'data-story-type': 'avatar' | 'image'
}>`
  overflow: hidden;

  &[data-story-type='avatar'] {
    background-color: white;
    min-width: 88px;
    min-height: 64px;
    max-width: 88px;
    max-height: 64px;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    padding: 0.5rem;

    @media ${screenSizes.tablet} {
      min-width: 120px;
      min-height: 87px;
    }
  }

  &[data-story-type='image'] {
    border-radius: 1rem 1rem 0 0;

    & img {
      height: auto;
      object-fit: cover;
    }
  }
`
